import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ChevronRight, Gift, Sparkles, Truck, ShieldCheck, Package, BadgeCheck, Zap, ShoppingBag, Megaphone, ArrowRight, Smartphone, Download } from "lucide-react";
import { api, inr } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useRealtime } from "@/context/RealtimeContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const pct = (p) => (p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0);

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [wishIds, setWishIds] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [content, setContent] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const { subscribe } = useRealtime();

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products?limit=500")]);
    const rank = (x) => (/electronic|electrical/i.test(`${x.id} ${x.name}`) ? 0 : 1);
    const ordered = [...c.data].sort((a, b) => rank(a) - rank(b));
    setCats(ordered); setProducts((p.data || []).filter((x) => x.in_stock)); // homepage par out-of-stock nahi
  };
  useEffect(() => { load(); api.get("/app-content").then((r) => setContent(r.data)).catch(() => {}); api.get("/media?category=document&limit=12").then((r) => setCatalog(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (user) { api.get("/wishlist").then((r) => setWishIds(r.data.map((x) => x.id))).catch(() => {}); api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {}); } }, [user]);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("product") || m.event?.startsWith("stock")) load(); }), [subscribe]);

  const toggleWish = async (p) => {
    if (!user) { nav("/login"); return; }
    const { data } = await api.post("/wishlist/toggle", { product_id: p.id });
    setWishIds((prev) => data.in_wishlist ? [...prev, p.id] : prev.filter((x) => x !== p.id));
    toast.success(data.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
  };

  const shown = activeCat === "all" ? products : products.filter((p) => p.category_id === activeCat);
  const byCat = (cid) => products.filter((p) => p.category_id === cid);
  // khaali categories (koi in-stock item nahi) customer ko nahi dikhni chahiye
  const visibleCats = cats.filter((c) => byCat(c.id).length > 0);
  const HL = { "cube-outline": Package, "shield-checkmark-outline": ShieldCheck, "car-outline": Truck, "rocket-outline": Truck };
  const hlIcon = (n) => { const I = HL[n] || BadgeCheck; return <I size={15} />; };
  const today = new Date().toISOString().slice(0, 10);
  const nextFest = (content?.festivals || []).filter((f) => f.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
  const festDays = nextFest ? Math.max(0, Math.ceil((new Date(nextFest.date) - new Date(today)) / 86400000)) : 0;
  const festCoupon = (content?.coupons || [])[0];
  const deals = [...products].filter((p) => pct(p) > 0).sort((a, b) => pct(b) - pct(a));
  // New Arrivals — app se sabse recently sync hue products (created_at desc)
  const newArrivals = [...products].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 8);
  const heroDeals = (deals.length >= 4 ? deals : products).slice(0, 4);
  const popular = (deals.length >= 3 ? deals : products).slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto px-3 lg:px-6 py-4 space-y-6">
      <h1 className="sr-only">Rani Super Market — online electronics &amp; home appliances store: LED TVs, washing machines, fans, mixer grinders, inverters &amp; daily essentials with fast home delivery</h1>
      {/* Rewards points strip */}
      {user && loyalty?.config?.enabled && (
        <button data-testid="rewards-strip" onClick={() => nav(loyalty.balance > 0 ? "/checkout" : "/rewards")} className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left text-white shadow-md hover:shadow-lg transition-shadow" style={{ background: "linear-gradient(90deg,#0B8E36,#0a7a2f)" }}>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Star size={20} className="fill-white" /></div>
          <div className="flex-1">
            <p className="font-extrabold text-base leading-tight" data-testid="rewards-strip-balance">{loyalty.balance > 0 ? `You have ${loyalty.balance} reward points` : "Earn reward points on every order"}</p>
            <p className="text-white/85 text-xs">{loyalty.balance > 0 ? `Worth ${inr(loyalty.wallet_value)} — redeem at checkout` : `Get ${loyalty.config.points_per_earn} pt per ₹${loyalty.config.earn_per_amount} spent`}</p>
          </div>
          <ChevronRight size={20} />
        </button>
      )}

      {/* Category navigation (design: icon row with underline active) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-6 min-w-max px-4 py-2.5">
          <Chip active={activeCat === "all"} onClick={() => setActiveCat("all")} icon={<Sparkles size={20} />} label="For You" />
          {visibleCats.map((c) => (
            <Chip key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} img={c.image} label={c.name} testid={`chip-${c.id}`} />
          ))}
        </div>
      </div>

      {/* Red pill promo strip (dynamic: festival ya default gift line) */}
      <button data-testid="promo-banner" onClick={() => nav("/search")} className="w-full bg-gradient-to-r from-red-600 via-red-600 to-rose-700 text-white font-bold py-2.5 px-6 rounded-full text-center text-sm md:text-base shadow-sm flex items-center justify-center gap-2 hover:opacity-95 transition-opacity">
        <Gift size={17} />
        <span>{nextFest ? `${nextFest.name} ${festDays === 0 ? "is today!" : `in ${festDays} day${festDays > 1 ? "s" : ""}`}${festCoupon ? ` — code ${festCoupon.code} for ${festCoupon.type === "percent" ? `${festCoupon.value}% off` : `₹${festCoupon.value} off`}` : ""}` : "Gifts for your loved ones"}</span>
      </button>

      {/* Hero banners (English, synced 2-way with Owner app) */}
      {content?.banners?.filter((b) => b.image)?.length > 0 && (
        <div data-testid="ad-banners" className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-3 px-3">
          {content.banners.filter((b) => b.image).map((b) => {
            const safeLink = b.link && /^https?:\/\//i.test(b.link) ? b.link : null;
            const cpn = (b.coupon_code || "").trim();
            const clickable = safeLink || cpn;
            const onBannerClick = () => {
              if (cpn) {
                localStorage.setItem("rsmart_pending_coupon", cpn);
                toast.success(`Coupon ${cpn} cart me lag gaya — checkout par apply hoga`);
                nav("/search");
              } else if (safeLink) {
                window.open(safeLink, "_blank", "noopener,noreferrer");
              }
            };
            return (
              <img key={b.id} data-testid={`home-banner-${b.id}`} src={b.image} alt={b.title || "Offer"} onClick={onBannerClick} className={`snap-start h-44 sm:h-60 lg:h-72 w-full sm:w-[80%] lg:w-[70%] shrink-0 object-cover rounded-2xl shadow-md border border-slate-200 ${clickable ? "cursor-pointer" : ""}`} />
            );
          })}
        </div>
      )}

      {/* Store highlights strip (synced from app) */}
      {content?.highlights?.length > 0 && (
        <div data-testid="highlights-strip" className="flex gap-2.5 overflow-x-auto no-scrollbar -mx-3 px-3">
          {content.highlights.map((h) => (
            <div key={h.id} className="flex items-center gap-2 shrink-0 bg-emerald-50 border border-emerald-100 rounded-full pl-1.5 pr-4 py-1.5">
              <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">{hlIcon(h.icon)}</span>
              <span className="text-xs font-semibold text-emerald-900 whitespace-nowrap">{h.text}</span>
            </div>
          ))}
        </div>
      )}

      {catalog.length > 0 && (
        <div data-testid="brochure-banner" onClick={() => nav("/downloads")}
          className="cursor-pointer relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-600 via-red-600 to-orange-500 text-white shadow-md p-4 md:p-5 flex items-center gap-4">
          <div className="absolute -right-8 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
          <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center"><Megaphone className="w-6 h-6 md:w-7 md:h-7" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-xs font-bold tracking-widest text-rose-100">NEW CATALOG</div>
            <div className="font-black text-base md:text-xl leading-tight">Naye brochures &amp; catalogs aa gaye!</div>
            <div className="text-rose-50 text-xs md:text-sm truncate">{catalog.length} document{catalog.length > 1 ? "s" : ""} — abhi free download karein.</div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 bg-white text-red-600 font-black text-xs md:text-sm px-3.5 md:px-5 py-2 md:py-2.5 rounded-xl shadow">View <ArrowRight size={16} /></span>
        </div>
      )}

      {/* Get the App promo (mobile + desktop) */}
      <div data-testid="home-app-promo" onClick={() => nav("/download-app")}
        className="cursor-pointer relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white shadow-md p-4 md:p-5 flex items-center gap-4">
        <div className="absolute -right-8 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
          <Smartphone className="w-6 h-6 md:w-7 md:h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] md:text-xs font-bold tracking-widest text-emerald-100">OFFICIAL ANDROID APP</div>
          <div className="font-black text-base md:text-xl leading-tight">Rani Super Market app download karein</div>
          <div className="text-emerald-50 text-xs md:text-sm truncate">Order, offers &amp; wishlist — sab ek app me. Bilkul free.</div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 bg-white text-emerald-700 font-black text-xs md:text-sm px-3.5 md:px-5 py-2 md:py-2.5 rounded-xl shadow">
          <Download size={16} /> <span className="hidden sm:inline">Download</span><span className="sm:hidden">Get</span>
        </span>
      </div>

      {/* Product grid for active category, or rails for "For You" */}
      {activeCat !== "all" ? (
        <section className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-slate-200">
          <h2 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight mb-3">{(visibleCats.find((c) => c.id === activeCat) || cats.find((c) => c.id === activeCat))?.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {shown.map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} />)}
          </div>
        </section>
      ) : (
        <>
          {/* Recently viewed */}
          <HomeSection title="Recently viewed" onSeeAll={() => nav("/search")}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {products.slice(0, 3).map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} />)}
            </div>
          </HomeSection>

          {/* Popular right now */}
          <HomeSection title="Popular right now" onSeeAll={() => nav("/search")}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {popular.map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} />)}
            </div>
          </HomeSection>

          {/* Rani Picks grid */}
          <HomeSection title="Rani Super Market Picks" badge="Quality Guarantee" onSeeAll={() => nav("/search")}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5">
              {products.slice(3, 9).map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} />)}
            </div>
          </HomeSection>

          {/* Interesting finds (emerald-tinted curated row) */}
          <section data-testid="interesting-finds" className="bg-emerald-50/70 border border-emerald-200/80 rounded-xl p-4 md:p-5 shadow-sm">
            <div className="flex items-center justify-between pb-3">
              <h2 className="text-lg md:text-xl font-extrabold text-slate-900 flex items-center gap-2 flex-wrap">Interesting finds <span className="text-xs font-normal text-slate-500">Curated trending gadgets, essentials & more</span></h2>
              <button onClick={() => nav("/search")} className="text-xs font-bold text-emerald-800 hover:underline flex items-center gap-1">View More <ChevronRight size={14} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              {(deals.length >= 4 ? deals.slice(3, 7) : products.slice(9, 13)).map((p, i) => (
                <button key={p.id} onClick={() => nav(`/product/${p.id}`)} className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm hover:shadow-md transition-shadow text-left flex flex-col justify-between">
                  <div className="relative w-full aspect-square bg-slate-50 rounded flex items-center justify-center p-2 mb-2 overflow-hidden">
                    <span className={`absolute top-2 left-2 ${["bg-indigo-600", "bg-pink-600", "bg-amber-600", "bg-purple-600"][i % 4]} text-white text-[9px] font-bold px-1.5 py-0.5 rounded`}>{["TRENDING", "POPULAR", "SUPER SAVER", "NEW ARRIVAL"][i % 4]}</span>
                    <img src={p.image || "/rani-brand.png"} alt={p.name} onError={(e) => { e.currentTarget.src = "/rani-brand.png"; }} className="w-full h-full object-contain hover:scale-105 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-800 line-clamp-1">{p.name}</h3>
                    <p className="text-[11px] text-slate-500 line-clamp-1">{p.brand || p.unit || "Rani Super Market"}</p>
                    <div className="flex items-baseline justify-between mt-2">
                      <span className="text-sm font-extrabold text-slate-900">{inr(p.price)}</span>
                      {pct(p) > 0 && <span className="text-[11px] text-emerald-600 font-bold">{pct(p)}% Off</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Shop by Brand */}
          {content?.brands?.length > 0 && (
            <section data-testid="shop-by-brand" className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-slate-200">
              <h2 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight mb-3">Shop by Brand</h2>
              <div className="flex gap-2.5 overflow-x-auto no-scrollbar -mx-3 px-3">
                {content.brands.map((b) => (
                  <button key={b.name} data-testid={`brand-${b.name}`} onClick={() => nav(`/search?brand=${encodeURIComponent(b.name)}`)} className="shrink-0 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-sm font-semibold text-slate-700 transition-colors">
                    {b.name} <span className="text-slate-400">({b.count})</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* New Arrivals — app se aaye latest products auto */}
          {newArrivals.length > 0 && (
            <HomeSection title="New Arrivals" badge="Fresh from Store" onSeeAll={() => nav("/search")} seeAllTestId="see-all-new-arrivals">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3.5">
                {newArrivals.map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} isNew />)}
              </div>
            </HomeSection>
          )}

          {/* Category rails */}
          {visibleCats.map((c) => {
            const items = byCat(c.id).slice(0, 5);
            if (!items.length) return null;
            return (
              <HomeSection key={c.id} title={c.name} onSeeAll={() => nav(`/category/${c.id}`)} seeAllTestId={`see-all-${c.id}`}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {items.map((p) => <ProductCard key={p.id} product={p} onWishlist={toggleWish} wished={wishIds.includes(p.id)} />)}
                </div>
              </HomeSection>
            );
          })}

          {/* Trust badges (brand guarantee strip — design same-to-same) */}
          <section data-testid="trust-badges" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 shrink-0 rounded-full bg-white/10 flex items-center justify-center border border-white/20"><ShieldCheck size={28} className="text-yellow-300" /></div>
              <div>
                <h4 className="font-extrabold text-base">Rani Super Market: 100% Quality Guarantee</h4>
                <p className="text-xs text-rose-100 mt-1">If you are not satisfied with freshness or quality of products, we offer an instant no-questions-asked replacement.</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 shrink-0 rounded-full bg-white/10 flex items-center justify-center border border-white/20"><BadgeCheck size={28} className="text-blue-300" /></div>
              <div>
                <h4 className="font-extrabold text-base">Rs. MART POS: 1-Year On-Site Support</h4>
                <p className="text-xs text-blue-200 mt-1">Full installation, GST database migration, and free barcode configuration across all retail hardware orders.</p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function HomeSection({ title, badge, onSeeAll, seeAllTestId, children }) {
  return (
    <section className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-slate-200">
      <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight">{title}</h3>
          {badge && <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">{badge}</span>}
        </div>
        <button data-testid={seeAllTestId} onClick={onSeeAll} className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">See all <ChevronRight size={14} /></button>
      </div>
      {children}
    </section>
  );
}

function Chip({ active, onClick, icon, img, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex flex-col items-center gap-1 shrink-0 pb-1 border-b-2 transition-colors ${active ? "border-blue-600" : "border-transparent"}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden transition-colors ${active ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
        {img ? <img src={img} alt={label} className="w-full h-full object-cover" /> : icon}
      </div>
      <span className={`text-xs font-medium ${active ? "font-bold text-blue-600" : "text-slate-700"}`}>{label}</span>
    </button>
  );
}
